import type { useRichTreeViewApiRef } from "@mui/x-tree-view";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import type { TFunction } from "core/entities";
import { useToaster } from "core/hooks/useToaster";
import { viewTransition } from "core/utils/viewTransition";
import type { DocNodeFileTreeItem } from "features/DMS/entities";
import type { UseFieldArrayReturn } from "react-hook-form";
import type {
  QuickLinkType,
  QuickLinkWidgetFormValues,
  SelectedQuickLink,
} from "./QuickLinkDefs";

function isSelectionDisabled(item: DocNodeFileTreeItem) {
  return item.type !== "Document";
}

export function QuickLinkDocNodeForm({
  apiRef,
  treeItems,
  selectedDocNodeIds,
  allSelectedQuickLinks,
  maxSelections,
  fieldArray,
  t,
}: {
  apiRef: ReturnType<typeof useRichTreeViewApiRef>;
  treeItems: DocNodeFileTreeItem[];
  selectedDocNodeIds: string[];
  allSelectedQuickLinks: SelectedQuickLink[];
  maxSelections: number;
  fieldArray: UseFieldArrayReturn<
    QuickLinkWidgetFormValues,
    "selectedQuickLinks"
  >;
  t: TFunction;
}) {
  const { toast } = useToaster();
  const maxLinksMessage = t("validation.maxLinks", {
    defaultValue: "You can select up to {{max}} quick links.",
    max: maxSelections,
  });
  return (
    <RichTreeView
      sx={{
        borderInlineEnd: {
          md: (theme) => `1px solid ${theme.palette.divider}`,
          xs: "none",
        },
        borderBlockEnd: {
          md: "none",
          xs: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
      apiRef={apiRef}
      items={treeItems}
      multiSelect
      checkboxSelection
      selectedItems={selectedDocNodeIds}
      onSelectedItemsChange={(_, itemIds) => {
        const customLinks = allSelectedQuickLinks.filter(
          (ql) => ql.type === "CustomLink"
        );
        if (itemIds.length + customLinks.length > maxSelections) {
          toast({ message: maxLinksMessage, severity: "warning" });
          return;
        }
        const docNodeLinks: SelectedQuickLink[] = itemIds.map((id) => ({
          id,
          type: "DocNode" as QuickLinkType,
        }));
        viewTransition(() => {
          fieldArray.replace([...docNodeLinks, ...customLinks]);
        });
      }}
      defaultExpandedItems={["grid"]}
      isItemSelectionDisabled={isSelectionDisabled}
    />
  );
}
